import { isEmpty } from 'lodash-es';
import moment from 'moment';
import React from 'react';
import { CartesianGrid, ComposedChart, Line, Tooltip, XAxis, YAxis } from 'recharts';

import {
  formatTooltipDate,
  formatTooltipValue,
  IDashboardSubChartMetric,
} from '~/src/dash/components/charts/common';
import { LoadingChart } from '~/src/dash/components/charts/LoadingChart';
import { Col, Cols, fontSizing, Row, Rows, spacing, Text } from '~/src/dash/publicaUI';
import { convertLargeNumbersToRoundedString } from '~/src/dash/utils/basic';
import { publicaColors } from '~/src/dash/utils/colors';
import { LoadingState, LoadingStateLoading } from '~/src/dash/utils/loading-states';
import { useMobileState, useMobileStore } from '~/src/dash/views/mobile/contexts';

const generateSubChartTickArray = (data: Array<Record<string, any>>) => {
  const max = data.length - 1;
  if (max < 1) {
    return [];
  }
  // if week, 12 hour, or 15 minutes, create 3 ticks, else create 5
  if (max === 6 || max === 11 || max === 14) {
    return [data[0].Date, data[Math.trunc(max / 2)].Date, data[max].Date];
  } else {
    return [
      data[0].Date,
      data[Math.trunc(max / 4)].Date,
      data[Math.trunc(max / 2)].Date,
      data[Math.trunc((3 * max) / 4)].Date,
      data[max].Date,
    ];
  }
};

const renderItems = (metrics: IDashboardSubChartMetric[], numItems: number) => {
  return metrics.map((metric) => {
    return (
      <Line
        name={metric.label}
        type='monotone'
        dataKey={metric.key}
        stroke={metric.color}
        fill={metric.color}
        strokeWidth={2}
        activeDot={{ r: 1 }}
        dot={numItems <= 15}
      />
    );
  });
};

interface ICustomTooltipProps {
  active: boolean;
  payload: any;
  label: string;
  metricsConfig: Record<string, IDashboardSubChartMetric>;
  grouping: number;
  currencySymbol: string;
}

const CustomTooltip = ({ active, payload, metricsConfig, currencySymbol }: ICustomTooltipProps) => {
  if (!active || isEmpty(payload)) return null;
  const date = formatTooltipDate(payload[0].payload.Date, 0);
  return (
    <Rows
      style={{
        backgroundColor: publicaColors.white,
        border: `1px solid ${publicaColors.lightGray}`,
        padding: spacing.small,
      }}
    >
      <Row
        noPadding
        style={{
          borderBottom: `1px solid ${publicaColors.lightGray}`,
          marginBottom: spacing.small,
        }}
      >
        <Text small bold>
          {date}
        </Text>
      </Row>
      {payload.map((item: any) => {
        const { dataKey, name } = item;
        const valueType = metricsConfig[dataKey].valueType;
        const value = formatTooltipValue(item.payload[dataKey], valueType, currencySymbol);
        return (
          <Row noPadding>
            <Cols
              gap={spacing.extraLarge}
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Col>
                <Cols gap='4px' style={{ alignItems: 'center' }}>
                  <Col paddingBottom='1px'>
                    <Text color={item.fill} style={{ fontSize: fontSizing.mini }}>
                      ⬤
                    </Text>
                  </Col>
                  <Col>
                    <Text small bold>
                      {name}
                    </Text>
                  </Col>
                </Cols>
              </Col>
              <Col>
                <Text small>{value}</Text>
              </Col>
            </Cols>
          </Row>
        );
      })}
    </Rows>
  );
};

interface IMobileMultiLineChartProps {
  loadState: LoadingState;
  data: Array<Record<string, any>>;
  grouping?: number;
  metrics: IDashboardSubChartMetric[];
  metricsCfg: Record<string, IDashboardSubChartMetric>;
}

export const MobileMultiLineChart = ({
  loadState,
  data = [],
  metrics,
  metricsCfg,
  grouping,
}: IMobileMultiLineChartProps) => {
  const { totalWidth } = useMobileState();
  const { currencySymbol } = useMobileStore();

  const formatTimestamp = (date: string | moment.Moment): string => {
    if (!date) {
      return '';
    }
    return moment(date)
      .utc()
      .format(grouping ? 'M/D' : 'HH:mm');
  };

  return (
    <div
      style={{
        width: '100%',
        height: '280px',
      }}
    >
      {loadState === LoadingStateLoading ? (
        <LoadingChart />
      ) : (
        <ComposedChart
          width={(totalWidth || 0) - 32}
          height={280}
          data={data}
          barGap={4}
          margin={{
            top: 0,
            right: 24,
            left: 0,
            bottom: 0,
          }}
        >
          <Tooltip
            content={
              // this bypasses an issue with automated props from recharts
              // @ts-ignore
              <CustomTooltip
                metricsConfig={metricsCfg}
                grouping={grouping || 0}
                currencySymbol={currencySymbol}
              />
            }
            cursor={{ fill: publicaColors.lightGray }}
          />
          <CartesianGrid strokeDasharray='3 3' vertical={false} />
          {renderItems(metrics, data.length)}
          <XAxis
            dataKey='Date'
            tick={{
              fontSize: 12,
              fill: 'black',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
            // @ts-ignore
            tickFormatter={formatTimestamp}
            ticks={generateSubChartTickArray(data)}
            domain={[0, data.length]}
          />
          <YAxis
            type='number'
            domain={[0, 'auto']}
            tick={{ fontSize: 12, fill: 'black', textAnchor: 'end' }}
            tickFormatter={convertLargeNumbersToRoundedString}
          />
        </ComposedChart>
      )}
    </div>
  );
};
